
import React, { Suspense, lazy, useEffect } from 'react';
import { useAppContext } from './hooks/useAppContext';
import { UserRole } from './types';

import PortalHeader from './components/layout/Header';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import SetupAssistant from './components/admin/SetupAssistant';
import AdminLoginPage from './components/auth/AdminLoginPage';
import OAuthConsent from './components/auth/OAuthConsent';
import NotificationContainer from './components/layout/NotificationContainer';

const AdminPortal = lazy(() => import('./components/admin/AdminPortal'));
const CustomerPortal = lazy(() => import('./components/customer/CustomerPortal'));
const PublicWebsite = lazy(() => import('./components/public/PublicWebsite'));

const AppShellLoader: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex min-h-[40vh] items-center justify-center rounded-3xl border border-gray-200 bg-white px-6 py-16 shadow-sm dark:border-slate-800 dark:bg-slate-950">
    <div className="text-center">
      <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" />
      <p className="text-sm font-medium text-gray-500 dark:text-slate-400">{label}</p>
    </div>
  </div>
);

const App: React.FC = () => {
  const { isAuthenticated, currentUser, logout, isLoading, setupRequired, isSetupMode, addNotification } = useAppContext();
  const currentPath = window.location.pathname;
  const isOauthConsentRoute = currentPath === '/oauth/consent';
  const isAdminLoginRoute = currentPath === '/admin';
  const isAdminUser = currentUser && [
    UserRole.ADMIN,
    UserRole.SUPER_ADMIN,
    UserRole.MANAGER,
    UserRole.ACCOUNTANT
  ].includes(currentUser.role);

  // Global Error Listeners for Async/Event Handlers
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled Promise Rejection:', event.reason);
      // Don't show toast for benign setup/auth errors we already handle in context
      const msg = event.reason?.message || String(event.reason);
      const lowerMsg = msg.toLowerCase();
      
      // Suppress network errors, setup prompts, and session expiration errors 
      // from the global toast system because AppContext handles them internally.
      if (
          lowerMsg.includes('setup required') || 
          lowerMsg.includes('schema cache') ||
          lowerMsg.includes('failed to fetch') ||
          lowerMsg.includes('networkerror') ||
          lowerMsg.includes('aborterror') ||
          lowerMsg.includes('load failed') ||
          lowerMsg.includes('loading chunk') ||
          lowerMsg.includes('resizeobserver') ||
          lowerMsg.includes('refresh token not found') ||
          lowerMsg.includes('invalid refresh token')
      ) {
        return;
      }

      addNotification(`Unexpected Error: ${msg}`, 'error');
    };

    const handleGlobalError = (event: ErrorEvent) => {
      console.error('Global Error:', event.error);
      const msg = event.message || "";
      const lowerMsg = msg.toLowerCase();
      if (
        lowerMsg.includes('refresh token not found') ||
        lowerMsg.includes('resizeobserver') ||
        lowerMsg.includes('script error')
      ) return;
      addNotification(`System Error: ${event.message}`, 'error');
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleGlobalError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleGlobalError);
    };
  }, [addNotification]);

  if (isOauthConsentRoute) {
    return <OAuthConsent />;
  }

  if (isLoading && !currentUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-slate-950">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="font-medium text-gray-500 dark:text-slate-400">Loading Application...</p>
        </div>
      </div>
    );
  }
  
  // If we detect missing tables OR manual setup mode, show the setup assistant
  if (setupRequired || isSetupMode) {
    return (
        <ErrorBoundary>
            <SetupAssistant />
            <NotificationContainer />
        </ErrorBoundary>
    );
  }

  if (!isAuthenticated || !currentUser) {
    if (isAdminLoginRoute) {
      return (
        <ErrorBoundary>
          <AdminLoginPage />
          <NotificationContainer />
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary>
        <Suspense fallback={<AppShellLoader label="Loading website..." />}>
          <PublicWebsite />
        </Suspense>
        <NotificationContainer />
      </ErrorBoundary>
    );
  }

  if (isAdminLoginRoute && !isAdminUser) {
    return (
      <ErrorBoundary>
        <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 dark:bg-slate-950">
          <div className="max-w-lg rounded-[2rem] border border-white bg-white p-8 text-center shadow-xl dark:border-slate-800 dark:bg-slate-950">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-indigo-500">Admin Access Only</p>
            <h1 className="mt-3 text-3xl font-black text-slate-950 dark:text-slate-100">This page is reserved for AGM admin accounts.</h1>
            <p className="mt-4 text-sm leading-7 text-slate-500 dark:text-slate-400">
              Your current session is a customer account. Use the main website to access the customer workspace.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => { window.location.href = '/'; }}
                className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-5 py-3 text-sm font-black text-slate-900 transition hover:bg-gray-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-900"
              >
                Go to main website
              </button>
              <button
                type="button"
                onClick={logout}
                className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-5 py-3 text-sm font-black text-white transition hover:bg-indigo-700"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
        <NotificationContainer />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-100 dark:bg-slate-950">
        <PortalHeader 
          currentUser={currentUser} 
          onLogout={logout}
        />
        <main className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
          <Suspense fallback={<AppShellLoader label={isAdminUser ? 'Loading admin workspace...' : 'Loading customer workspace...'} />}>
            {isAdminUser ? (
              <AdminPortal />
            ) : (
              <CustomerPortal />
            )}
          </Suspense>
        </main>
        <NotificationContainer />
      </div>
    </ErrorBoundary>
  );
};

export default App;
